import type { PublicAppConfig } from '@protoss/shared';
import { onAuthStateChanged } from 'firebase/auth';
import * as React from 'react';
import { buildBrandAssetPath, buildFaviconPngPath } from '../lib/branding';
import { firebaseAuth } from '../lib/firebase';
import { getPreAuthConfig } from '../lib/preAuthConfig';

// Static fallback shown before the DB-driven config is loaded.
// Brand fields are generic defaults - the actual values are seeded into
// workspace.config and loaded from the API after the user authenticates.
const STATIC_APP_CONFIG: PublicAppConfig = {
  deploymentKey: import.meta.env.VITE_DEPLOYMENT_KEY || 'app-dev',
  brandKey: 'cosow',
  environment:
    import.meta.env.VITE_APP_ENV === 'prod'
      ? 'prod'
      : import.meta.env.VITE_APP_ENV === 'uat'
        ? 'uat'
        : import.meta.env.VITE_APP_ENV === 'demo'
          ? 'demo'
          : 'dev',
  productName: 'Workspace',
  companyName: 'Workspace',
  headerWordmark: 'Workspace',
  documentTitle: 'Workspace',
  metaDescription: 'Operational workspace',
  authHeadline: 'Operational context for modern systems',
  authSupportingText:
    'Capture decisions, workflows, and system context in one operational platform.',
  landingHeadline: 'Operational context for modern systems',
  landingSupportingText:
    'Capture decisions, workflows, and system context in one operational platform.',
  aiAssistantName: 'Assistant',
  supportEmail: undefined,
  supportUrl: undefined,
};

interface PublicAppConfigContextValue {
  config: PublicAppConfig;
  isLoading: boolean;
}

const PublicAppConfigContext = React.createContext<PublicAppConfigContextValue | null>(null);

function upsertMetaDescription(content: string): void {
  let meta = document.querySelector('meta[name="description"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'description');
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

function upsertFavicon(icoHref: string, pngHref: string): void {
  let icoLink = document.querySelector('link[rel="icon"][type="image/x-icon"]');
  if (!icoLink) {
    icoLink = document.createElement('link');
    icoLink.setAttribute('rel', 'icon');
    icoLink.setAttribute('type', 'image/x-icon');
    document.head.appendChild(icoLink);
  }
  icoLink.setAttribute('href', icoHref);

  let pngLink = document.querySelector('link[rel="icon"][type="image/png"]');
  if (!pngLink) {
    pngLink = document.createElement('link');
    pngLink.setAttribute('rel', 'icon');
    pngLink.setAttribute('type', 'image/png');
    document.head.appendChild(pngLink);
  }
  pngLink.setAttribute('href', pngHref);
}

export function PublicAppConfigProvider({ children }: { children: React.ReactNode }) {
  const [config, setConfig] = React.useState<PublicAppConfig>(() => ({
    ...STATIC_APP_CONFIG,
    ...getPreAuthConfig().brandConfig,
  }));
  const [isLoading, setIsLoading] = React.useState(false);
  const faviconHref = buildBrandAssetPath(config.brandKey, 'favicon');
  const faviconPngHref = buildFaviconPngPath(config.brandKey);

  React.useEffect(() => {
    // Subscribe to Firebase auth state. When the user authenticates, fetch
    // the workspace brand config from the API and merge it over the static
    // defaults. On sign-out, reset to static defaults.
    const unsubscribe = onAuthStateChanged(firebaseAuth, (user) => {
      if (!user) {
        setConfig({ ...STATIC_APP_CONFIG, ...getPreAuthConfig().brandConfig });
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      const configUrl = (() => {
        if (import.meta.env.DEV) {
          const slug = new URLSearchParams(window.location.search).get('workspace');
          if (slug) return `/api/workspace/admin/app-config?workspace=${encodeURIComponent(slug)}`;
        }
        return '/api/workspace/admin/app-config';
      })();
      void user
        .getIdToken()
        .then((token) =>
          fetch(configUrl, {
            headers: { Authorization: `Bearer ${token}` },
          })
        )
        .then(async (res) => {
          if (!res.ok) return; // 403 for non-admins - keep static defaults
          const { config: appConfig } = (await res.json()) as { config: PublicAppConfig };
          setConfig(appConfig);
        })
        .catch(() => {
          // Network error or unexpected failure - keep static defaults
        })
        .finally(() => {
          setIsLoading(false);
        });
    });

    return unsubscribe;
  }, []);

  React.useEffect(() => {
    document.title = config.documentTitle;
    upsertMetaDescription(config.metaDescription);
    upsertFavicon(faviconHref, faviconPngHref);
  }, [config.documentTitle, config.metaDescription, faviconHref, faviconPngHref]);

  const value = React.useMemo(() => ({ config, isLoading }), [config, isLoading]);

  return (
    <PublicAppConfigContext.Provider value={value}>{children}</PublicAppConfigContext.Provider>
  );
}

export function usePublicAppConfig() {
  const context = React.useContext(PublicAppConfigContext);
  if (!context) {
    throw new Error('usePublicAppConfig must be used within PublicAppConfigProvider');
  }
  return context;
}
